// src/components/BookingForm.js
import React, { useState, useEffect, useCallback, useRef } from 'react';
import emailjs from '@emailjs/browser';
import './BookingForm.css';
import { auth, db } from '../firebase';
import {
  collection,
  query,
  where,
  getDocs
} from 'firebase/firestore';
import DatePicker from 'react-multi-date-picker';

export default function BookingForm({ entityId, entityName }) {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    dates: [],
    message: '',
  });

  const [success, setSuccess] = useState(false);
  const [checking, setChecking] = useState(false);
  const [dateFeedback, setDateFeedback] = useState('');
  const lastCheckedDatesRef = useRef([]);

  const formatDate = (dateObj) => dateObj.format('DD/MM/YYYY');

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged((user) => {
      if (user) {
        setFormData((prev) => ({
          ...prev,
          email: user.email || '',
          name: user.displayName || '',
        }));
      }
    });
    return () => unsubscribe();
  }, []);

  const checkDatesAvailability = useCallback(async (datesToCheck) => {
    // ✅ Fix : Vérification plus robuste
    if (!datesToCheck || !Array.isArray(datesToCheck) || datesToCheck.length === 0) {
      setDateFeedback('');
      return;
    }

    setChecking(true);
    try {
      const formattedDates = datesToCheck.map(d => formatDate(d));
      const available = [];
      const taken = [];

      for (const date of formattedDates) {
        const q = query(
          collection(db, 'bookings'),
          where('entityId', '==', entityId),
          where('date', '==', date)
        );
        const snapshot = await getDocs(q);
        if (snapshot.empty) {
          available.push(date);
        } else {
          taken.push(date);
        }
      }

      const validDates = datesToCheck.filter(d => available.includes(formatDate(d)));

      setFormData(prev => ({
        ...prev,
        dates: validDates
      }));

      if (taken.length > 0 && available.length > 0) {
        setDateFeedback(`⚠️ Les dates suivantes ont été retirées car elles sont déjà réservées : ${taken.join(', ')}`);
      } else if (taken.length > 0 && available.length === 0) {
        setDateFeedback('❌ Toutes les dates sélectionnées sont déjà réservées.');
      } else {
        setDateFeedback('');
      }
    } catch (error) {
      console.error('Erreur vérification dates:', error);
    } finally {
      setChecking(false);
    }
  }, [entityId]);

  const handleDateChange = (dates) => {
    // ✅ Fix : Assurer que dates est toujours un tableau
    const safeDates = dates || [];
    setFormData(prev => ({ ...prev, dates: safeDates }));
    setDateFeedback('');
    setSuccess(false);
  };

  const handleDatePickerClose = () => {
    // ✅ Fix : Vérification null/undefined
    const currentDates = formData.dates || [];
    if (!Array.isArray(currentDates)) return;
    
    const currentFormatted = currentDates.map(d => formatDate(d)).sort();
    const lastFormatted = [...lastCheckedDatesRef.current].sort();

    const hasChanged = currentFormatted.length !== lastFormatted.length ||
      currentFormatted.some((date, i) => date !== lastFormatted[i]);

    if (hasChanged && currentDates.length > 0) {
      lastCheckedDatesRef.current = [...currentFormatted];
      checkDatesAvailability(currentDates);
    }
  };

  const handleChange = (e) => {
    setFormData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
    if (success) setSuccess(false);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // ✅ Fix : Vérification sécurisée
    const currentDates = formData.dates || [];
    if (!Array.isArray(currentDates) || currentDates.length === 0) {
      alert("Aucune date disponible sélectionnée.");
      return;
    }

    const formattedDates = currentDates.map(d => formatDate(d));

    const templateParams = {
      entityName,
      name: formData.name,
      email: formData.email,
      phone: formData.phone,
      date: formattedDates.join(', '),
      message: formData.message,
    };

    try {
      await emailjs.send(
        'SERVICE_ID',
        'template_pgdt8yl',
        templateParams,
        'udMka3n0REP1MEHEo'
      );
      setSuccess(true);
      setFormData({
        name: auth.currentUser?.displayName || '',
        email: auth.currentUser?.email || '',
        phone: '',
        dates: [], // ✅ Fix : Toujours un tableau
        message: '',
      });
      setDateFeedback('');
      lastCheckedDatesRef.current = [];
    } catch (error) {
      console.error('Erreur EmailJS:', error);
      alert("Une erreur est survenue lors de l'envoi. Veuillez réessayer.");
    }
  };

  // ✅ Fix : Sécuriser les accès aux propriétés
  const safeDates = formData.dates || [];
  const datesLength = Array.isArray(safeDates) ? safeDates.length : 0;

  return (
    <div className="booking-form">
      <h3>Demander {entityName} pour un booking</h3>

      {success && (
        <p className="success-msg">✅ Votre demande a été envoyée avec succès !</p>
      )}

      <form onSubmit={handleSubmit}>
        <input
          type="text"
          name="name"
          placeholder="Votre nom"
          value={formData.name}
          onChange={handleChange}
          required
        />

        <input
          type="email"
          name="email"
          placeholder="Votre email"
          value={formData.email}
          onChange={handleChange}
          required
        />

        <input
          type="tel"
          name="phone"
          placeholder="Votre numéro de téléphone"
          value={formData.phone}
          onChange={handleChange}
          required
        />

        <label htmlFor="dates">Dates souhaitées</label>
        <DatePicker
          multiple
          value={safeDates}
          onChange={handleDateChange}
          onClose={handleDatePickerClose}
          format="DD/MM/YYYY"
          placeholder="Sélectionnez les dates"
          minDate={new Date()}
          weekDays={['Di', 'Lu', 'Ma', 'Me', 'Je', 'Ve', 'Sa']}
          months={['Janvier', 'Février', 'Mars', 'Avril', 'Mai', 'Juin', 'Juillet', 'Août', 'Septembre', 'Octobre', 'Novembre', 'Décembre']}
          arrow={true}
          containerStyle={{
            width: "100%",
          }}
          calendarProps={{
            style: {
              backgroundColor: "#ff0000ff",
              border: "1px solid #333",
              borderRadius: "12px",
              boxShadow: "0 8px 32px rgba(0, 0, 0, 0.5)",
              padding: window.innerWidth <= 600 ? "12px" : "16px",
              marginTop: "8px",
              overflow: "hidden"
            }
          }}
          mapDays={({ date, today, selectedDate, isSameDate }) => {
            let props = {
              style: {
                color: "#e0e0e0",
                borderRadius: "8px",
                transition: "all 0.2s ease",
                margin: "2px",
                fontWeight: "500"
              }
            };

            if (date.format() === today.format()) {
              props.style = {
                ...props.style,
                backgroundColor: "#333",
                color: "#ff9800",
                fontWeight: "600"
              };
            }

            // ✅ Fix : Vérifier que selectedDate est un tableau
            const safeSelectedDate = Array.isArray(selectedDate) ? selectedDate : [];
            if (safeSelectedDate.some(d => isSameDate(date, d))) {
              props.style = {
                ...props.style,
                backgroundColor: "#ff9800",
                color: "#121212",
                fontWeight: "600",
                boxShadow: "0 2px 8px rgba(255, 152, 0, 0.3)"
              };
            }

            return props;
          }}
          renderButton={(direction, handleClick) => (
            <button 
              type="button"
              onClick={handleClick}
              className="date-picker-nav-btn"
              style={{
                background: '#2c2c2c',
                border: '1px solid #444',
                borderRadius: '8px',
                width: window.innerWidth <= 600 ? '36px' : '40px',
                height: window.innerWidth <= 600 ? '36px' : '40px',
                cursor: 'pointer',
                color: '#ff9800',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: window.innerWidth <= 600 ? '18px' : '20px',
                fontWeight: 'bold',
                transition: 'all 0.2s ease',
                margin: '0 4px',
                padding: '0'
              }}
              onMouseEnter={(e) => {
                e.target.style.backgroundColor = '#ff9800';
                e.target.style.color = '#121212';
                e.target.style.transform = 'scale(1.05)';
              }}
              onMouseLeave={(e) => {
                e.target.style.backgroundColor = '#2c2c2c';
                e.target.style.color = '#ff9800';
                e.target.style.transform = 'scale(1)';
              }}
            >
              {direction === "right" ? "›" : "‹"}
            </button>
          )}
        />

        {checking && <div className="checking-msg">Vérification des dates...</div>}
        {dateFeedback && <div className="date-taken-msg">{dateFeedback}</div>}

        <textarea
          name="message"
          placeholder="Message ou détails supplémentaires"
          value={formData.message}
          onChange={handleChange}
          rows={4}
        />

        <button type="submit" disabled={checking || datesLength === 0}>
          Envoyer la demande
        </button>
      </form>
    </div>
  );
}